import { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ChapterMarker, RevealText } from './shared';
import { BROWN } from '../assets';

export default function Ch7Inspection() {
  const areaRef = useRef(null);
  const [pos, setPos] = useState({ x: 50, y: 45 });
  const [active, setActive] = useState(false);

  const onMove = (e) => {
    const r = areaRef.current.getBoundingClientRect();
    setPos({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
  };

  const onTouch = (e) => {
    const t = e.touches[0];
    if (!t) return;
    const r = areaRef.current.getBoundingClientRect();
    setActive(true);
    setPos({ x: ((t.clientX - r.left) / r.width) * 100, y: ((t.clientY - r.top) / r.height) * 100 });
  };

  return (
    <section id="ch-7" data-testid="chapter-7" className="relative min-h-screen bg-[#0d0b09] px-6 md:px-16 py-28 md:py-40">
      <ChapterMarker num="07" title="Quality Inspection" />
      <div className="md:grid md:grid-cols-12 md:gap-16 items-center">
        <div className="md:col-span-4">
          <RevealText className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight">
            Under the<br /><span className="italic text-[#B89B72]">light.</span>
          </RevealText>
          <p className="font-body font-light text-sm text-[#A3A3A3] max-w-xs mt-8 leading-relaxed">
            Forty-seven checkpoints per jacket. Move your cursor across the piece —
            this is how our inspectors see it, one beam at a time.
          </p>
          <div className="mt-10 space-y-3">
            {['Seam tension', 'Grain uniformity', 'Hardware pull test', 'Colour fastness'].map((c, i) => (
              <motion.div
                key={c}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center gap-3 font-accent text-[10px] text-[#A3A3A3]"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#B89B72]" />
                {c} <span className="text-[#8C5A35] ml-auto">PASS</span>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="md:col-span-8 mt-14 md:mt-0">
          <div
            ref={areaRef}
            data-testid="inspection-light-area"
            data-hover
            onMouseMove={onMove}
            onTouchMove={onTouch}
            onTouchStart={onTouch}
            onMouseEnter={() => setActive(true)}
            onMouseLeave={() => setActive(false)}
            className="relative aspect-[16/11] overflow-hidden border border-[#2A2A2A]"
          >
            <img src={BROWN.inspect} alt="Brown suede bomber under inspection" className="absolute inset-0 w-full h-full object-cover" />
            <div
              className="absolute inset-0 transition-opacity duration-300"
              style={{
                background: active
                  ? `radial-gradient(circle 180px at ${pos.x}% ${pos.y}%, rgba(10,10,10,0) 0%, rgba(10,10,10,0.55) 45%, rgba(10,10,10,0.93) 100%)`
                  : 'rgba(10,10,10,0.75)',
              }}
            />
            {!active && (
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="font-accent text-[10px] text-[#B89B72] border border-[#B89B72]/40 px-5 py-2.5 backdrop-blur-sm bg-[#0A0A0A]/40">
                  <span className="hidden md:inline">Move cursor to inspect</span>
                  <span className="md:hidden">Touch &amp; drag to inspect</span>
                </span>
              </div>
            )}
            <div className="absolute bottom-4 left-4 font-accent text-[9px] text-[#A3A3A3]">
              LOT PT-2411 — INSPECTOR NO. 07
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
